import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import { Box, Button, CssBaseline, Paper, Stack, ThemeProvider, Typography } from '@mui/material';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { PropsWithChildren } from 'react';
import { I18nextProvider, useTranslation } from 'react-i18next';
import rtlPlugin from 'stylis-plugin-rtl';
import { prefixer } from 'stylis';
import i18n from '@app/i18n';
import { createHelixTheme } from '@design-system/theme/createHelixTheme';
import { useUiStore } from '@shared/store/uiStore';
import { getCurrentUser } from '@features/auth/api/auth';
import { useAuthStore } from '@features/auth/store/authStore';
import { shouldRetryApiQuery } from '@shared/api/rateLimitCooldown';
import { getSessionGeneration, SESSION_STORAGE_KEY } from '@shared/api/sessionTokens';

const ltrCache = createCache({ key: 'helix-ltr' });
const rtlCache = createCache({ key: 'helix-rtl', stylisPlugins: [prefixer, rtlPlugin] });

export function AppProviders({ children }: PropsWithChildren) {
  const mode = useUiStore((state) => state.mode);
  const locale = useUiStore((state) => state.locale);
  const direction = locale === 'ar' ? 'rtl' : 'ltr';
  const authStatus = useAuthStore((state) => state.status);
  const completeLogin = useAuthStore((state) => state.completeLogin);
  const logout = useAuthStore((state) => state.logout);
  const beginSessionCheck = useAuthStore((state) => state.beginSessionCheck);
  const [bootstrapAttempt, setBootstrapAttempt] = useState(0);
  const [bootstrapFailure, setBootstrapFailure] = useState<number | null>(null);
  const observedGeneration = useRef(getSessionGeneration());
  const previousAuthStatus = useRef(authStatus);
  const bootstrapFailed = authStatus === 'loading' && bootstrapFailure === bootstrapAttempt;
  const retrySession = useCallback(() => {
    setBootstrapAttempt((current) => (current === bootstrapFailure ? current + 1 : current));
  }, [bootstrapFailure]);
  const theme = useMemo(() => createHelixTheme(mode, direction), [direction, mode]);
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: { staleTime: 30_000, retry: shouldRetryApiQuery, refetchOnWindowFocus: false },
          mutations: { retry: 0 },
        },
      }),
  );

  useEffect(() => {
    document.documentElement.lang = locale;
    document.documentElement.dir = direction;
    void i18n.changeLanguage(locale);
  }, [direction, locale]);

  useEffect(() => {
    if (authStatus !== 'loading') return;
    queryClient.clear();
    const generation = getSessionGeneration();
    observedGeneration.current = generation;
    if (generation === null) {
      logout();
      return;
    }
    let active = true;
    const controller = new AbortController();
    void getCurrentUser(controller.signal)
      .then((user) => {
        if (!active || controller.signal.aborted || useAuthStore.getState().status !== 'loading')
          return;
        const current = getSessionGeneration();
        if (current === null) {
          logout();
          return;
        }
        if (current === generation) completeLogin(user);
      })
      .catch(() => {
        if (!active || controller.signal.aborted || useAuthStore.getState().status !== 'loading')
          return;
        const current = getSessionGeneration();
        if (current === null) {
          logout();
          return;
        }
        if (current !== generation) return;
        // The transport clears only definitively invalid credentials, using the
        // exact token pair. A late 401 may belong to an already-rotated pair.
        setBootstrapFailure(bootstrapAttempt);
      });
    return () => {
      active = false;
      controller.abort();
    };
  }, [authStatus, bootstrapAttempt, completeLogin, logout, queryClient]);

  useEffect(() => {
    if (
      previousAuthStatus.current !== authStatus &&
      (authStatus === 'unauthenticated' || authStatus === 'awaitingOtp')
    )
      queryClient.clear();
    previousAuthStatus.current = authStatus;
    if (authStatus !== 'loading') observedGeneration.current = getSessionGeneration();
  }, [authStatus, queryClient]);

  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key !== SESSION_STORAGE_KEY && event.key !== null) return;
      if (event.storageArea && event.storageArea !== localStorage) return;
      const current = getSessionGeneration();
      if (current === observedGeneration.current) return;
      observedGeneration.current = current;
      queryClient.clear();
      if (current === null) {
        logout();
        return;
      }
      // Never reuse the previous account's identity/permissions after another-tab login.
      beginSessionCheck();
      setBootstrapAttempt((attempt) => attempt + 1);
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [beginSessionCheck, logout, queryClient]);

  useEffect(() => {
    if (!bootstrapFailed) return;
    window.addEventListener('online', retrySession);
    return () => window.removeEventListener('online', retrySession);
  }, [bootstrapFailed, retrySession]);

  return (
    <I18nextProvider i18n={i18n}>
      <CacheProvider value={direction === 'rtl' ? rtlCache : ltrCache}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <QueryClientProvider client={queryClient}>
            {bootstrapFailed ? <SessionCheckRecovery onRetry={retrySession} /> : children}
          </QueryClientProvider>
        </ThemeProvider>
      </CacheProvider>
    </I18nextProvider>
  );
}

function SessionCheckRecovery({ onRetry }: { onRetry: () => void }) {
  const { t } = useTranslation();
  return (
    <Box component="main" sx={{ minHeight: '100dvh', display: 'grid', placeItems: 'center', p: 3 }}>
      <Paper variant="outlined" sx={{ p: 3, maxWidth: 480, width: '100%', borderRadius: 2 }}>
        <Stack spacing={2} role="alert" aria-labelledby="session-check-title">
          <Typography id="session-check-title" component="h1" variant="h5">
            {t('auth.sessionCheckTitle')}
          </Typography>
          <Typography color="text.secondary">{t('auth.sessionCheckDescription')}</Typography>
          <Button
            type="button"
            variant="contained"
            onClick={onRetry}
            sx={{ alignSelf: 'flex-start' }}
          >
            {t('common.retry')}
          </Button>
        </Stack>
      </Paper>
    </Box>
  );
}
